import type {
  ConciseAmendOperation,
  ConciseAnnulOperation,
  ConciseCreateOperation,
  ConciseHideOperation,
  ConciseOperation,
} from '@festina/configuration-general-types/configuration'
import type { Optional } from '@festina-life/core'

import type { ConfigurationItem } from '../../../../types/common'

export const isConciseCreateOperation = (
  operation: ConciseOperation,
): operation is ConciseCreateOperation =>
  operation._type === 'ConciseCreateOperation'

export const isConciseHideOperation = (
  operation: ConciseOperation,
): operation is ConciseHideOperation =>
  operation._type === 'ConciseHideOperation'

export const isConciseAmendOperation = (
  operation: ConciseOperation,
): operation is ConciseAmendOperation =>
  operation._type === 'ConciseAmendOperation'

export const isConciseAnnulOperation = (
  operation: ConciseOperation,
): operation is ConciseAnnulOperation =>
  operation._type === 'ConciseAnnulOperation'

export const isConciseEntityOperation = (
  operation: ConciseOperation,
): operation is
  | ConciseAmendOperation
  | ConciseCreateOperation
  | ConciseHideOperation =>
  isConciseCreateOperation(operation) ||
  isConciseHideOperation(operation) ||
  isConciseAmendOperation(operation)

export const slugifyChangesetName = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30)
    .replace(/-+$/g, '')

export const conciseOperationTypeToConfigurationItem = (
  operation: ConciseOperation,
): Optional<Exclude<ConfigurationItem, 'annulOperation'>> => {
  if (isConciseHideOperation(operation)) {
    if (operation.entityType === 'GlMap') {
      return 'hideGlMap'
    } else if (operation.entityType === 'Template') {
      return 'hideTemplate'
    }
  }

  if (isConciseAmendOperation(operation)) {
    return operation.entityType === 'ParameterValue'
      ? 'parameterValueAmendment'
      : undefined
  }

  if (isConciseCreateOperation(operation)) {
    switch (operation.entityType) {
      case 'Template':
        return 'template'
      case 'ParameterDefinition':
        return 'parameterDefinition'
      case 'ParameterValue':
        return 'parameterValue'
      case 'RegisteredTenant':
        return 'tenant'
      case 'EnumDefinition':
        return 'enumDefinition'
      case 'EnumLiteral':
        return 'enumLiteral'
      case 'GlMap':
        return 'glMap'
      case 'ReconciliationSpec':
        return 'reconciliationSpec'
      case undefined:
      case null:
      default:
        return undefined
    }
  }

  return undefined
}
